import { Button } from '@/components/ui/button';
import { useLanguage } from '@/hooks/useLanguage';
import { Download, Users, Phone, Factory } from 'lucide-react';

export const Hero = () => {
  const { t } = useLanguage();

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    // ปรับจาก items-center → items-start และเสริม pt ให้ดันลงจากขอบบนเล็กน้อย
    <section id="home" className="relative min-h-screen flex items-start justify-center overflow-hidden pt-10 sm:pt-14">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-teal-50" />

      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-0" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-secondary rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-1000" />
        <div className="absolute bottom-10 left-1/2 w-72 h-72 bg-primary rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000" />
      </div>

      {/* ลด padding แนวตั้งลงเล็กน้อย (เดิม py-20) */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left space-y-8 animate-in fade-in slide-in-from-left duration-1000">
            {/* โลโก้แยกออกมา และจัดกลางเฉพาะ mobile */}
            <img
              src="/images/Logo.png"
              alt="Company Logo"
              className="mx-auto lg:mx-0 w-32 sm:w-40 md:w-48 h-auto"
            // ถ้าต้องการ lazy: loading="lazy"
            />

            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-in fade-in slide-in-from-bottom duration-1000 delay-200">
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  {t.hero.headline}
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed animate-in fade-in slide-in-from-bottom duration-1000 delay-400">
                {t.hero.subheadline}
              </p>
              {/* Badge: Developer & Manufacturer */}
              <div className="mt-4 flex justify-center lg:justify-start">
                <span className="inline-flex items-center gap-3 px-6 py-3 rounded-full 
                   text-base sm:text-lg font-bold
                   bg-red-500 text-white shadow-lg ring-2 ring-red-700">
                  <img
                    src="/images/thai-flag.svg"
                    alt="Thai Flag"
                    className="w-6 h-4 object-cover rounded-sm shadow-sm"
                  />
                  Developer & Manufacturer in Thailand
                </span>
              </div>

            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-6 text-sm text-gray-500 animate-in fade-in slide-in-from-bottom duration-1000 delay-600">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>ISO Standards</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse delay-200" />
                <span>Bangkok, Thailand</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse delay-400" />
                <span>Est. 2023</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-in fade-in slide-in-from-bottom duration-1000 delay-800">
              <Button
                size="lg"
                onClick={() => scrollToSection('#contact')}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t.hero.ctaContact}
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={() => window.open('#', '_blank')}
                className="border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 text-lg font-semibold transition-all duration-300 transform hover:scale-105"
              >
                <Download className="w-5 h-5 mr-2" />
                {t.hero.ctaCatalog}
              </Button>

              <Button
                size="lg"
                variant="ghost"
                onClick={() => scrollToSection('#contact')}
                className="text-primary hover:bg-primary/10 px-8 py-3 text-lg font-semibold transition-all duration-300 transform hover:scale-105"
              >
                <Users className="w-5 h-5 mr-2" />
                {t.hero.ctaDistributor}
              </Button>
            </div>
          </div>

          {/* Visual */}
          <div className="relative animate-in fade-in slide-in-from-right duration-1000 delay-300">
            <div className="relative mx-auto max-w-lg">
              {/* ใช้ภาพผลิตภัณฑ์แทน mockup */}
              <img
                src="/images/products-hero.png" // เปลี่ยนเป็น path ที่คุณวางไฟล์
                alt="Chemical Indicators Products"
                className="w-full h-auto object-contain select-none pointer-events-none drop-shadow-2xl"
                loading="eager"
                decoding="async"
              />

              {/* ถ้าอยากเพิ่ม effect พิเศษ */}
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-secondary rounded-full opacity-60 animate-pulse animation-delay-500" />
              <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-primary rounded-full opacity-60 animate-pulse animation-delay-1200" />
              <div className="absolute top-1/4 -right-2 w-4 h-4 bg-secondary rounded-full opacity-40 animate-pulse animation-delay-800" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce animation-delay-2000">
        <button
          onClick={() => scrollToSection('#about')}
          className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center hover:border-primary transition-colors"
          aria-label="Scroll to next section"
        >
          <div className="w-1 h-3 bg-gray-400 rounded-full mt-2 animate-pulse" />
        </button>
      </div>
    </section>
  );
};
